import { useRef, useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';

import { usePixiApp } from '../../services/pixiApp/pixiAppProvider';
import GameManager from '../../features/gameplay/managers/gameManager';
import VsScreenManager from '../../features/gameplay/managers/vsScreenManager';
import DecksManager from '../../features/decks/managers/decksManager';
import DeckEditorManager from '../../features/decks/managers/DeckEditorManager';
import AssetsLoader from '../../features/gameplay/utils/assetsLoader';
import { useApi } from '../../services/api/ApiProvider';
import { useSocket } from '../../services/socket/SocketContext';
import { deleteDeck as apiDeleteDeck } from '../../services/api/apiService';

import { useAuth } from '../../services/auth/authProvider';

// useWakeLock: a React hook for requesting/releasing wake lock
function useWakeLock() {
    const wakeLockRef = useRef(null);

    const canWakeLock = useCallback(() => 'wakeLock' in navigator, []);

    const requestWakeLock = useCallback(async () => {
        if (canWakeLock()) {
            try {
                wakeLockRef.current = await navigator.wakeLock.request('screen');
                wakeLockRef.current.addEventListener('release', () => {
                    wakeLockRef.current = null;
                });
                return wakeLockRef.current;
            } catch (err) {
                // silently fail or handle error as desired
                wakeLockRef.current = null;
            }
        }
        return null;
    }, [canWakeLock]);

    const releaseWakeLock = useCallback(async () => {
        if (wakeLockRef.current) {
            try {
                await wakeLockRef.current.release();
            } catch (err) {
                // handle error if necessary
            } finally {
                wakeLockRef.current = null;
            }
        }
    }, []);

    return { requestWakeLock, releaseWakeLock };
}


function GamePage() {
    const { roomId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [searchParams] = useSearchParams();
    const findMatchMode = searchParams.get('findMatchMode');
    const isFindMatchMode = findMatchMode === '1';

    const pixiContainerRef = useRef(null);
    const vsScreenManagerRef = useRef(null);
    const decksManagerRef = useRef(null);
    const gameManagerRef = useRef(null);
    const gameBoardActiveRef = useRef(false);

    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingDeckAssets, setIsLoadingDeckAssets] = useState(true);
    const [isGameStarted, setIsGameStarted] = useState(false);
    const [hasJoined, setHasJoined] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [roomState, setRoomState] = useState();

    const { requestWakeLock, releaseWakeLock } = useWakeLock();

    const { userName } = useAuth();

    const { app, setAutoResize } = usePixiApp();
    const socket = useSocket();
    const { decks, fetchDecks, setSelectedDeck, setCoins } = useApi();

    const copyRoomName = () => {
        navigator.clipboard.writeText(roomId);
    };

    const openDeckSelection = () => {
        navigate(
            { pathname: location.pathname, search: location.search, hash: 'decks' },
            { state: location.state },
        );
    };

    const closeDeckSelection = () => {
        navigate(-1);
    };

    const createNewDeck = async () => {
        await DeckEditorManager.loadAssets();
        navigate('/decks/create');
    };

    const editDeck = async (deckId) => {
        await DeckEditorManager.loadAssets();
        navigate(`/decks/edit/${deckId}`);
    };

    const selectDeck = (deckId) => {
        const deck = decks.find(d => d.id === deckId);
        if (!deck) return;

        setSelectedDeck(deck);
        vsScreenManagerRef.current?.changeDeck(deck);
        socket.emit('selectDeck', { deckId: deck.id });
        navigate(-1);
    };

    const confirmDeleteDeck = async (deckId) => {
        await apiDeleteDeck(deckId);
        await fetchDecks();
    };

    useEffect(() => {
        const onAppResize = () => {
            if (gameBoardActiveRef.current && gameManagerRef.current) {
                gameManagerRef.current.resize(app.screen.width, app.screen.height);
                return;
            }

            if (vsScreenManagerRef.current) {
                vsScreenManagerRef.current.resize(app.screen.width, app.screen.height);
            }

            if (decksManagerRef.current) {
                decksManagerRef.current.buildMainLayout(app.screen.width, app.screen.height);
                decksManagerRef.current.buildDeckList();
            }
        };

        const initVsScreen = async () => {
            if (app && socket && pixiContainerRef.current && !vsScreenManagerRef.current) {
                pixiContainerRef.current.addEventListener('touchstart', (e) => e.preventDefault(), { passive: false });

                let selectedDeckId = localStorage.getItem('selectedDeck');
                let deck = selectedDeckId && decks.find(d => d.id === selectedDeckId);
                if (!deck) {
                    selectedDeckId = decks[0].id;
                    deck = decks.find(d => d.id === selectedDeckId);
                    localStorage.setItem('selectedDeck', selectedDeckId);
                }

                await socket.emit('joinRoom', { roomName: roomId, username: userName, deckId: selectedDeckId });

                socket.on('joinRoom', async (data) => {
                    console.log('joinRoom', data);

                    socket.emit('selectDeck', { deckId: selectedDeckId });

                    const opponent = data.users.find(user => user.userId !== socket.id);
                    console.log('opponent', opponent);

                    vsScreenManagerRef.current = new VsScreenManager(isFindMatchMode, roomId);
                    vsScreenManagerRef.current.attachToPixiApp(app);
                    pixiContainerRef.current.appendChild(app.canvas);

                    const decksManager = new DecksManager(decks, selectedDeckId);

                    await Promise.all([
                        VsScreenManager.loadAssets(),
                        decksManager.loadAssets(),
                    ]);

                    vsScreenManagerRef.current.buildVsScreen(userName, deck, opponent?.username, opponent?.readyToPlay);

                    decksManager.buildMainLayout(app.screen.width, app.screen.height);
                    decksManager.buildDeckList();
                    decksManagerRef.current = decksManager;

                    if (location.hash === '#decks') {
                        decksManager.attachToPixiApp(app);
                    }

                    window.addEventListener('appResize', onAppResize);
                    setAutoResize(true);

                    vsScreenManagerRef.current.onCopyRoomNameButtonClicked = copyRoomName;

                    setHasJoined(true);

                    if (isFindMatchMode) {
                        socket.emit('readyToPlay', {});
                    }

                    socket.on('roomStateUpdated', (data) => {
                        console.log('roomStateUpdated', data);
                        setRoomState(data);
                    });

                    socket.on('gameStarted', async (data) => {
                        const { assets } = data;
                        await AssetsLoader.loadDecks([assets]);
                        setIsLoadingDeckAssets(false);
                    });
                });
            }
        }

        const initGame = () => {
            if (app && socket && !gameManagerRef.current) {
                gameManagerRef.current = new GameManager(isFindMatchMode);
                const initPromise = gameManagerRef.current.init(socket, localStorage);

                const setupGame = () => {
                    const updateQueue = [];
                    socket.on('gameStateUpdate', async (newGameState) => {
                        console.log(newGameState);

                        updateQueue.push(newGameState);
                        if (updateQueue.length === 1) {
                            while (updateQueue.length > 0) {
                                await initPromise;
                                await gameManagerRef.current.updateGameState(updateQueue[0]);
                                setIsGameStarted(true);
                                updateQueue.shift();
                            }
                        }
                    });

                    socket.on('matchRewards', (reward) => {
                        gameManagerRef.current?.showMatchRewards(reward);
                        setCoins(reward.balance);
                    });

                    socket.on('actionError', (error) => {
                        console.log(error);
                    });
                }

                setupGame();
            }
        }

        Promise.all([initGame(), initVsScreen()]).then(() => setIsLoading(false));

        return () => {
            gameBoardActiveRef.current = false;
            setAutoResize(false);
            window.removeEventListener('appResize', onAppResize);

            if (vsScreenManagerRef.current && app) {
                vsScreenManagerRef.current.detachFromPixiApp(app);
            }

            if (decksManagerRef.current && app) {
                decksManagerRef.current.detachFromPixiApp(app);
            }

            if (gameManagerRef.current && app) {
                gameManagerRef.current.cleanup();
                gameManagerRef.current.detachFromPixiApp(app);
            }

            if (socket) {
                socket.emit('leaveRoom');
                socket.off('joinRoom');
                socket.off('roomStateUpdated');
                socket.off('gameStateUpdate');
                socket.off('matchRewards');
                socket.off('actionError');
            }
        };
    }, [app, socket, pixiContainerRef]);

    useEffect(() => {
        if (vsScreenManagerRef.current) {
            vsScreenManagerRef.current.updateRoomState(socket.id, roomState);
        }
    }, [roomState]);

    useEffect(() => {
        if (decksManagerRef.current && decks) {
            decksManagerRef.current.setDecks(decks);
        }
    }, [decks]);

    useEffect(() => {
        if (!decksManagerRef.current) return;

        if (location.hash === '#decks') {
            decksManagerRef.current.attachToPixiApp(app);
        } else if (location.hash === '') {
            decksManagerRef.current.detachFromPixiApp(app);
        }
    }, [location]);

    useEffect(() => {
        const startGame = async () => {
            if (isGameStarted && !isLoading && !isLoadingDeckAssets) {
                console.log('gameManagerRef.current.serverGameState', gameManagerRef.current.serverGameState);

                const opponentLeader = gameManagerRef.current.serverGameState.opponent.leader;
                await vsScreenManagerRef.current.showOpponentLeaderAnimation(opponentLeader);

                decksManagerRef.current?.detachFromPixiApp(app);
                vsScreenManagerRef.current?.detachFromPixiApp(app);
                gameBoardActiveRef.current = true;
                gameManagerRef.current?.attachToPixiApp(app);
                gameManagerRef.current?.startBackgroundMusic();
                gameManagerRef.current?.refreshScene();
                gameManagerRef.current?.resize(app.screen.width, app.screen.height);
                await gameManagerRef.current?.startGame();
            }
        };

        startGame();
    }, [isGameStarted, isLoading, isLoadingDeckAssets]);

    const handleClickReady = () => {
        if (!hasJoined) return;

        socket.emit('readyToPlay', {});
    };

    const handleClickCancel = () => {
        if (!hasJoined) return;

        if (isFindMatchMode) {
            navigate(-1);
        } else {
            socket.emit('cancelReadyToPlay', {});
        }
    };

    const handleClickBack = () => {
        if (isGameStarted) return;

        navigate(-1);
    };

    useEffect(() => {
        if (!hasJoined) return;

        if (vsScreenManagerRef.current) {
            vsScreenManagerRef.current.onSelectDeckButtonClicked = openDeckSelection;
            vsScreenManagerRef.current.onReadyButtonClicked = handleClickReady;
            vsScreenManagerRef.current.onCancelButtonClicked = handleClickCancel;
            vsScreenManagerRef.current.onBackButtonClicked = handleClickBack;
        }

        if (decksManagerRef.current) {
            decksManagerRef.current.onBackButtonClick = closeDeckSelection;
            decksManagerRef.current.onNewDeckButtonClick = createNewDeck;
            decksManagerRef.current.onEditDeckButtonClick = editDeck;
            decksManagerRef.current.onSelectDeckButtonClick = selectDeck;
            decksManagerRef.current.onConfirmDeleteDeck = confirmDeleteDeck;
        }
    }, [hasJoined, decks, handleClickReady, handleClickCancel, handleClickBack]);

    const handleRematchClicked = () => {
        setAutoResize(true);
        navigate(
            { pathname: location.pathname, search: location.search },
            {
                replace: true,
                state: { ...location.state, gameRemountKey: Date.now() },
            },
        );
    };

    const handleQuitMatchClicked = () => {
        navigate(-1);
    };

    useEffect(() => {
        if (gameManagerRef.current) {
            gameManagerRef.current.onRematchClicked = handleRematchClicked;
            gameManagerRef.current.onQuitMatchClicked = handleQuitMatchClicked;
        }
    }, [handleRematchClicked, handleQuitMatchClicked]);

    useEffect(() => {
        requestWakeLock();

        return () => {
            releaseWakeLock();
        };
    }, []);

    return (
        <div style={{ width: '100dvw', height: '100dvh', overflow: 'hidden' }} ref={pixiContainerRef} />
    );
}

export default GamePage;
